import { Loader2, RotateCcw } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  AUDIENCE_WIZARD_BUTTON_CLASS,
  AUDIENCE_WIZARD_BUTTON_VARIANT,
} from '@/lib/constants/audience-wizard'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CountrySearchSelect } from '@/pages/AudiencesNewPage/components/mapping/CountrySearchSelect'
import type { IdentityFieldSummary } from '@/types/components/audiences/import-session'
import { FIELD_CONSTANT_OPTIONS, identityFieldLabel } from '../lib/required-fields-ui'

interface IdentitySetConstantPanelProps {
  field: IdentityFieldSummary
  draftConstantValue: string
  workspaceDirty: boolean
  isPatching: boolean
  onDraftConstantValueChange: (value: string) => void
  onSubmit: () => void
  onUndo?: () => void
}

export function IdentitySetConstantPanel({
  field,
  draftConstantValue,
  workspaceDirty,
  isPatching,
  onDraftConstantValueChange,
  onSubmit,
  onUndo,
}: IdentitySetConstantPanelProps) {
  const { t } = useTranslation('app')
  const fieldLabel = identityFieldLabel(field.canonicalId, field.label, t)
  return (
    <div className="space-y-6">
      {field.canonicalId === 'std_geo_country' ? (
        <div className="space-y-1.5">
          <p className="text-sm text-muted-foreground">{t('audienceCreate.identityField.std_geo_country')}</p>
          <CountrySearchSelect
            value={draftConstantValue}
            onChange={onDraftConstantValueChange}
            disabled={isPatching}
            className="h-11 w-full rounded-md text-base"
          />
        </div>
      ) : (
        <div className="space-y-1.5">
          <p className="text-sm text-muted-foreground">{fieldLabel}</p>
          <Select
            value={draftConstantValue || '__unset__'}
            onValueChange={(value) =>
              onDraftConstantValueChange(value === '__unset__' ? '' : value)
            }
            disabled={isPatching}
          >
            <SelectTrigger className="h-11 rounded-md text-base">
              <SelectValue
                placeholder={t('audienceCreate.chooseField', { field: fieldLabel.toLowerCase() })}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__unset__" className="text-base">{t('audienceCreate.selectEllipsis')}</SelectItem>
              {(FIELD_CONSTANT_OPTIONS[field.canonicalId] ?? []).map((option) => (
                <SelectItem key={option} value={option} className="text-base">
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex items-center justify-center gap-4 pt-5">
        <Button
          type="button"
          variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
          onClick={onSubmit}
          disabled={
            isPatching ||
            draftConstantValue.length === 0 ||
            (!workspaceDirty && field.state === 'ready_from_constant')
          }
          className={cn('h-10 px-4', AUDIENCE_WIZARD_BUTTON_CLASS)}
        >
          {isPatching ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('audienceCreate.saving')}
            </>
          ) : (
            t('audienceCreate.saveChanges')
          )}
        </Button>
        {onUndo ? (
          <Button
            type="button"
            variant="outline"
            className="h-10 rounded-md border px-4 text-base font-medium tracking-tight border-input-focus text-foreground dark:text-input-focus hover:bg-input-focus/10"
            onClick={onUndo}
            disabled={isPatching || !workspaceDirty}
          >
            <RotateCcw className="mr-2 h-3.5 w-3.5" />
            {t('audienceCreate.undoChanges')}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
